import { Event, Extension, Runtime, ajax } from 'main.core';
import { EventEmitter, type BaseEvent } from 'main.core.events';

const SELECTOR = '.bx-im-list-container-vibe-code-catalog-button__container';
const EVENTS_ATTRIBUTE = 'data-bx-vibe-code-catalog-events';
const CATALOG_EXTENSION = 'vibecodeconnector.catalog';
const BANNER_DISPATCHER_EXTENSION = 'ui.banner-dispatcher';
const SELF_EXTENSION = 'vibecodeconnector.im-button-binder';
const MARK_SHOWN_ACTION = 'vibecodeconnector.Catalog.markCatalogShown';


const CatalogEvent = Object.freeze({
	request: 'im:vibe-code-catalog:request',
	stateChanged: 'im:vibe-code-catalog:state-changed',
});

class ImButtonBinder
{
	#observer: MutationObserver | null = null;
	#boundElement: HTMLElement | null = null;
	#catalog = null;
	#catalogLoading: Promise | null = null;
	#previewUserId: number | null = null;
	#isOpen: boolean = false;
	#mustAutoShow: boolean = false;
	#autoShownInSession: boolean = false;
	#autoShowOnClose: (() => void) | null = null;

	init(): void
	{
		const settings = Extension.getSettings(SELF_EXTENSION);
		const rawPreview = settings.get('previewUserId', null);
		this.#previewUserId = typeof rawPreview === 'number' && rawPreview > 0 ? rawPreview : null;
		this.#mustAutoShow = settings.get('catalog_must_be_shown', false) === true;

		EventEmitter.subscribe(CatalogEvent.request, this.#handleRequest);

		this.#observer = new MutationObserver(() => this.#tryAttachInside(window.document));
		this.#observer.observe(window.document, { childList: true, subtree: true });
		this.#tryAttachInside(window.document);
	}

	#handleRequest = (event: BaseEvent): void => {
		const { open, node } = event.getData();
		if (open === true)
		{
			void this.#open(node instanceof HTMLElement ? node : null);
		}
		else
		{
			this.#close();
		}
	};

	#tryAttachInside(root: ParentNode): void
	{
		const node = root.querySelector?.(SELECTOR);
		if (!(node instanceof HTMLElement) || node === this.#boundElement)
		{
			return;
		}

		// Schedule the auto-show on the first appearance of the button node in both
		// modes: the event mode of the real Vue button (the if branch) and direct
		// binding (the else branch). Schedule before disconnect so the event mode
		// also gets a chance to enter the queue.
		this.#scheduleAutoShow(node);

		if (node.hasAttribute(EVENTS_ATTRIBUTE))
		{
			this.#observer?.disconnect();
			this.#observer = null;
		}
		else
		{
			this.#boundElement = node;
			Event.bind(node, 'click', this.#handleDomClick);
		}
	}

	#scheduleAutoShow(node: HTMLElement): void
	{
		if (!this.#mustAutoShow || this.#autoShownInSession)
		{
			return;
		}

		this.#autoShownInSession = true;

		Runtime.loadExtension(BANNER_DISPATCHER_EXTENSION)
			.then(({ BannerDispatcher }) => {
				BannerDispatcher.normal.toQueue((onDone) => {
					// The catalog may already be open (opened manually before the
					// dispatcher slot became free): do not open it twice, just
					// release the queue slot.
					if (this.#isOpen)
					{
						onDone();

						return;
					}

					this.#autoShowOnClose = () => {
						this.#markCatalogShown();
						onDone();
					};

					// The deferred node may be detached (the Vue button re-rendered);
					// open without an anchor instead of binding to a stale node.
					const anchor = node.isConnected ? node : null;

					this.#open(anchor).catch(() => {
						// Opening failed: release the queue slot so other banners are
						// not blocked, but keep the shown flag untouched so the catalog
						// can be shown again in another session. Reset the close hook so
						// a later close does not double-fire it.
						this.#autoShowOnClose = null;
						onDone();
					});
				});
			})
			.catch(() => {
				// The banner dispatcher failed to load and there is no retry this
				// session; swallow the rejection to avoid an unhandled rejection.
			});
	}

	#markCatalogShown(): void
	{
		try
		{
			void ajax.runAction(MARK_SHOWN_ACTION).catch(() => {});
		}
		catch (error)
		{
			// The catalog view is recorded in the background: a network error must not break the UI.
		}
	}

	#handleDomClick = (event: MouseEvent): void => {
		const node = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;

		if (this.#isOpen)
		{
			this.#close();
		}
		else
		{
			void this.#open(node);
		}
	};

	async #open(node: HTMLElement | null): Promise<void>
	{
		const catalog = await this.#ensureCatalog();
		catalog.show(node);
		this.#isOpen = true;
		EventEmitter.emit(CatalogEvent.stateChanged, { active: true });
	}

	#close(): void
	{
		this.#catalog?.close();
	}

	#ensureCatalog(): Promise<any>
	{
		if (this.#catalog !== null)
		{
			return Promise.resolve(this.#catalog);
		}

		if (this.#catalogLoading === null)
		{
			const previewUserId = this.#previewUserId;
			this.#catalogLoading = Runtime
				.loadExtension(CATALOG_EXTENSION)
				.then((exports) => {
					return this.#detectIsEmpty(previewUserId)
						.then(({ isEmpty }) => {

							this.#catalog = new exports.Catalog({
								isEmpty,
								previewUserId,
								onClose: () => { this.#closeCatalog(); },
							});

							return this.#catalog;
						}
					);
				})
			;
		}

		return this.#catalogLoading;
	}

	#closeCatalog()
	{
		this.#isOpen = false;
		EventEmitter.emit(CatalogEvent.stateChanged, { active: false });

		if (this.#autoShowOnClose !== null)
		{
			const onClose = this.#autoShowOnClose;
			this.#autoShowOnClose = null;
			onClose();
		}
	}

	#detectIsEmpty(previewUserId: ?string): Promise<any>
	{
		if (previewUserId === null)
		{
			return Promise.resolve({ isEmpty: null });
		}

		return new Promise((resolve) => {
			try
			{
				ajax.runAction('vibecodeconnector.Catalog.isEmpty', {
					data: { previewUserId },
				})
				.then((result) => {
					const isEmpty = result?.data?.isEmpty;
					resolve({ isEmpty });
				}).catch(() => {
					resolve({ isEmpty: null });
				});
			}
			catch (error)
			{
				resolve({ isEmpty: null });
			}
		});
	}
}

export { ImButtonBinder };

new ImButtonBinder().init();
